import { useEffect } from "react";

/**
 * Locks body scroll when a modal is mounted / active.
 *
 * Uses the `position: fixed` technique which is the only approach that
 * reliably prevents background scroll on iOS Safari. The current scroll
 * position is saved before locking and restored on cleanup so the user
 * returns to the same place when the modal closes.
 *
 * For modals that are conditionally rendered (mounted only when open),
 * call with no argument or pass `true`. For modals controlled by a
 * boolean prop, pass that prop.
 */
export function useBodyScrollLock(isLocked = true): void {
  useEffect(() => {
    if (!isLocked) return;

    const scrollY = window.scrollY;
    const body = document.body;

    // Save original styles
    const originalOverflow = body.style.overflow;
    const originalPosition = body.style.position;
    const originalTop = body.style.top;
    const originalWidth = body.style.width;

    // Lock scroll -- iOS Safari requires position: fixed + top offset
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";

    return () => {
      // Restore original styles
      body.style.overflow = originalOverflow;
      body.style.position = originalPosition;
      body.style.top = originalTop;
      body.style.width = originalWidth;

      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, [isLocked]);
}
